import {createNavigationContainerRef} from '@react-navigation/native';

export const navigationRef = createNavigationContainerRef();

/**
 * Module-level breadcrumb of the most recently visited routes.
 *
 * Why this exists: the `NavigationContainer` is mounted INSIDE the app-level
 * `ErrorBoundary` (see `app/components/AppProviders.jsx`). When a screen throws,
 * the boundary swaps in `ErrorFallback` and the `NavigationContainer` unmounts,
 * so by the time `componentDidCatch` (and the error reporter) runs,
 * `navigationRef.getCurrentRoute()` returns nothing. Recording the route on every
 * navigation state change into this module-level cache means a crash report can
 * still state exactly WHICH screen the user was on and HOW they got there — the
 * single most important piece of "where is it wrong" context that was missing.
 *
 * @type {{name: string, params: (object|null)}|null}
 */
let lastKnownRoute = null;

/** @type {Array<{name: string, at: string}>} */
let routeHistory = [];

const MAX_ROUTE_HISTORY = 15;

/**
 * Record the currently active route. Called from `NavigationContainer`'s
 * `onStateChange` so the breadcrumb stays in sync with what the user sees.
 *
 * @param {{name?: string, params?: object}|null|undefined} route - The route
 *   returned by `navigationRef.getCurrentRoute()`.
 * @returns {void}
 */
export const recordCurrentRoute = (route) => {
    if (!route || !route.name) {
        return;
    }

    lastKnownRoute = {name: route.name, params: route.params || null};

    routeHistory.push({name: route.name, at: new Date().toISOString()});
    if (routeHistory.length > MAX_ROUTE_HISTORY) {
        routeHistory = routeHistory.slice(-MAX_ROUTE_HISTORY);
    }
};

/**
 * Resolve the current route, preferring the live navigation container (most
 * accurate) and falling back to the last route recorded before the tree
 * unmounted into the error fallback.
 *
 * @returns {{name: string, params: (object|null)}|null}
 */
export const getCurrentRouteInfo = () => {
    try {
        if (navigationRef.isReady?.() && typeof navigationRef.getCurrentRoute === 'function') {
            const current = navigationRef.getCurrentRoute();
            if (current?.name) {
                return {name: current.name, params: current.params || null};
            }
        }
    } catch (_) {
        // Fall through to the cached breadcrumb below.
    }

    return lastKnownRoute;
};

/**
 * Return a shallow copy of the recent route breadcrumb (oldest -> newest).
 *
 * @returns {Array<{name: string, at: string}>}
 */
export const getRouteHistory = () => routeHistory.slice();

export const navigateWhenReady = (name, params) => {
    if (!name || !navigationRef.isReady()) {
        return false;
    }

    navigationRef.navigate(name, params);
    return true;
};
